/* ============================================================================
   HOUND — SAFE AREA METRICS
   ============================================================================
   Publishes the real height of the fixed site header onto :root as
   --hound-header-h, so every full-viewport RPG surface can reserve clearance for
   it instead of guessing.

   Why measure rather than hardcode: the header is 104px on desktop and 84px on
   mobile today, but that is a consequence of the nav's content and padding, not
   a declared constant. Any of the numbers behind it can change without anyone
   remembering that four other sections encode it. Every surface that used a
   hardcoded clearance was already wrong — .quest-container started 84px ABOVE the
   header's bottom edge, so the nav sat on top of the quest tabs.

   Cheap: one measurement on load, one per resize (rAF-coalesced), one when the
   header's own box changes.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = '.hound-header__menu-wrapper, .header, header';
  var raf = 0;

  function headerEl() {
    var nodes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      // Only a pinned header steals space from the surfaces below it; a header
      // that scrolls away needs no permanent reservation.
      if (cs.position !== 'fixed' && cs.position !== 'sticky' && cs.position !== 'absolute') continue;
      var r = el.getBoundingClientRect();
      if (r.height > 8 && r.top <= 4) return el;
    }
    return null;
  }

  // The den header's design height at the 1728x1117 reference. Same number as
  // --rpg-head-h in assets/hound-shop-card.css, which is what actually paints.
  var REF_HEAD_H = 124;

  function stageScale() {
    var s = parseFloat(getComputedStyle(document.documentElement)
      .getPropertyValue('--rpg-stage-s'));
    return s > 0 ? s : 1;
  }

  function measure() {
    var el = headerEl();
    var h = el ? Math.round(el.getBoundingClientRect().height) : 0;
    // Guard against a mid-animation or collapsed read producing a silly value
    // that would then be baked into every surface's padding.
    //
    // The ceiling was a flat 220 when no header could be taller than 124px.
    // The den now scales its header with the stage (assets/hound-shop-card.css
    // sizes it as 124px * --rpg-stage-s), so a 4K den header is legitimately
    // 222px and a flat 220 silently REJECTED it - the token then held a stale
    // 124px and every surface under it reserved ~100px too little. Raising the
    // constant to 400 fixed 4K and re-broke the same way above s ~ 3.2, which
    // is just the original bug with a bigger number in it.
    //
    // So DERIVE it: the guard is "no more than the design header on THIS
    // stage, plus the slack the original guard allowed" - 220/124 = 1.77, kept
    // as 1.8. At s = 1 that is 223px, i.e. the old behaviour on every surface
    // that is not the den; at s = 1.79 it is 400; and it can no longer go
    // stale, because the thing it is a multiple of is the same number the
    // header itself is a multiple of.
    if (h < 32 || h > REF_HEAD_H * stageScale() * 1.8) return;
    var cur = document.documentElement.style.getPropertyValue('--hound-header-h');
    if (cur !== h + 'px') {
      document.documentElement.style.setProperty('--hound-header-h', h + 'px');
    }
  }

  function schedule() {
    if (raf) return;
    raf = requestAnimationFrame(function () { raf = 0; measure(); });
  }

  function boot() {
    measure();
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', function () {
      setTimeout(measure, 250);
    });
    // Webfonts land after first paint and can change the nav's line box.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);

    var el = headerEl();
    if (el && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(schedule).observe(el);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.houndSafeArea = { measure: measure };
})();
